/**
 * Análise de produto (A174, 15/10/2026): quanto as pacientes usam cada parte
 * do app, por marca, com números nossos — 100% das pessoas, não amostra.
 *
 * Por enquanto no Firestore, enquanto o volume é pequeno; a migração para uma
 * estrutura de dados própria (BigQuery) leva os eventos brutos sem mexer no
 * app. Duas coleções:
 *
 * - `eventosDeUso`: cada evento, com validade (`expiraEm`, TTL de 13 meses).
 *   A pessoa vai como hash (`pessoa`), nunca o id; as propriedades são ids e
 *   valores de lista, nunca texto livre.
 * - `resumosDeUso` (id == dia): o dia somado, por marca — o que o painel lê.
 *   Dia passado é fechado uma vez; o de hoje é calculado na hora.
 *
 * O catálogo abaixo é fechado: evento ou propriedade fora dele é recusado.
 * É o que mantém os números comparáveis no tempo e longe de dado sensível.
 */
import { z } from 'zod';
import { brandIdField } from './brand';

export type GrupoDeEvento = 'USO' | 'BOTOES' | 'BLOQUEIOS' | 'FUNCIONALIDADES';

export interface DefinicaoDeEvento {
  rotulo: string;
  grupo: GrupoDeEvento;
  /** Propriedades aceitas. Valores: ids, slugs e chaves de lista. */
  props: readonly string[];
  /** Gravado pela API (mais confiável); o app não pode mandar. */
  doServidor?: boolean;
}

export const EVENTOS_DE_USO = {
  sessao_iniciada: { rotulo: 'Sessão iniciada', grupo: 'USO', props: [] },
  tela_vista: { rotulo: 'Tela vista', grupo: 'USO', props: ['tela', 'id'] },
  botao_clicado: { rotulo: 'Botão tocado', grupo: 'BOTOES', props: ['acao', 'destino'] },
  bloqueio_visto: { rotulo: 'Bloqueio visto', grupo: 'BLOQUEIOS', props: ['regra', 'alvo', 'item'] },
  bloqueio_acao: { rotulo: 'Ação no bloqueio', grupo: 'BLOQUEIOS', props: ['regra', 'alvo', 'acao'] },
  formulario_enviado: { rotulo: 'Formulário enviado', grupo: 'FUNCIONALIDADES', props: ['formulario'], doServidor: true },
  checkpoint_concluido: { rotulo: 'Checkpoint concluído', grupo: 'FUNCIONALIDADES', props: ['checkpoint'], doServidor: true },
  evento_inscricao: { rotulo: 'Inscrição em evento', grupo: 'FUNCIONALIDADES', props: ['evento'], doServidor: true },
} as const satisfies Record<string, DefinicaoDeEvento>;

export type EventoDeUso = keyof typeof EVENTOS_DE_USO;
export const NOMES_DE_EVENTOS = Object.keys(EVENTOS_DE_USO) as EventoDeUso[];

/** Valor de propriedade aceito: id, slug ou chave — sem espaço, sem texto livre. */
export const VALOR_DE_PROPRIEDADE = /^[A-Za-z0-9_\-.:]{1,120}$/;

/**
 * As telas da paciente, pelo caminho. A mesma divisão das telas do
 * Contentsquare, mais o id do que foi aberto (curso, revista, ferramenta,
 * checkpoint) — é o que conta "conteúdo aberto" sem um evento à parte.
 */
export const TELAS_DE_USO: Record<string, { rotulo: string; funcionalidade: FuncionalidadeDeUso | null }> = {
  inicio: { rotulo: 'Início', funcionalidade: null },
  conteudo: { rotulo: 'Conteúdo', funcionalidade: 'CONTEUDO' },
  lista_de_conteudo: { rotulo: 'Lista de conteúdos', funcionalidade: 'CONTEUDO' },
  busca: { rotulo: 'Busca', funcionalidade: 'CONTEUDO' },
  curso: { rotulo: 'Curso ou vídeo', funcionalidade: 'CONTEUDO' },
  trilha: { rotulo: 'Trilha', funcionalidade: 'CONTEUDO' },
  revista: { rotulo: 'Revista', funcionalidade: 'CONTEUDO' },
  cuidado: { rotulo: 'Cuidado', funcionalidade: 'CUIDADO' },
  checkpoint: { rotulo: 'Checkpoint', funcionalidade: 'CUIDADO' },
  ferramenta: { rotulo: 'Ferramenta', funcionalidade: 'FERRAMENTAS' },
  lembretes: { rotulo: 'Lembretes', funcionalidade: 'CUIDADO' },
  chat: { rotulo: 'Chat', funcionalidade: 'CHAT' },
  comunidade: { rotulo: 'Comunidade', funcionalidade: 'COMUNIDADE' },
  agenda: { rotulo: 'Agenda', funcionalidade: 'AGENDA' },
  notificacoes: { rotulo: 'Notificações', funcionalidade: null },
  formulario: { rotulo: 'Formulário', funcionalidade: null },
  perfil: { rotulo: 'Perfil', funcionalidade: 'PERFIL' },
  vela: { rotulo: 'Acender vela', funcionalidade: 'EXPERIENCIAS' },
  cadastro: { rotulo: 'Cadastro', funcionalidade: null },
};

export const FUNCIONALIDADES_DE_USO = {
  CONTEUDO: 'Conteúdo',
  CUIDADO: 'Cuidado (jornada e checkpoints)',
  FERRAMENTAS: 'Ferramentas de cuidado',
  CHAT: 'Chat',
  COMUNIDADE: 'Comunidade',
  AGENDA: 'Agenda',
  PERFIL: 'Perfil',
  EXPERIENCIAS: 'Experiências especiais',
} as const;
export type FuncionalidadeDeUso = keyof typeof FUNCIONALIDADES_DE_USO;

/**
 * A tela de um caminho do app, e o id do que foi aberto. `null` = fora da
 * área da paciente (painéis, login), que não conta.
 */
export function telaDoCaminho(caminho: string, busca = ''): { tela: string; id?: string } | null {
  const partes = caminho.split('/').filter(Boolean);
  if (partes[0] === 'cadastro') return { tela: 'cadastro' };
  if (partes[0] !== 'eu') return null;
  const [, sec, sub] = partes;
  const q = new URLSearchParams(busca);
  const id = (v: string | null | undefined) => (v && VALOR_DE_PROPRIEDADE.test(v) ? v : undefined);
  switch (sec) {
    case undefined:
      return { tela: 'inicio' };
    case 'content':
      return { tela: 'conteudo' };
    case 'conteudos':
      return { tela: 'lista_de_conteudo', id: id(sub) };
    case 'search':
      return { tela: 'busca' };
    case 'course':
      return { tela: 'curso', id: id(q.get('courseId') ?? q.get('id')) };
    case 'trail':
      return { tela: 'trilha', id: id(q.get('trailId') ?? q.get('id')) };
    case 'magazine':
      return { tela: 'revista', id: id(sub) };
    case 'care':
      return { tela: 'cuidado' };
    case 'care-item':
      return { tela: 'checkpoint', id: id(sub) };
    case 'care-tool':
      return { tela: 'ferramenta', id: id(sub) };
    case 'care-reminders':
      return { tela: 'lembretes' };
    case 'chat':
      return { tela: 'chat' };
    case 'community':
      return { tela: 'comunidade' };
    case 'agenda':
      return { tela: 'agenda' };
    case 'notificacoes':
      return { tela: 'notificacoes' };
    case 'adhesion-form':
      return { tela: 'formulario', id: id(q.get('formId')) };
    case 'vela':
      return { tela: 'vela' };
    case 'profile':
    case 'myProfile':
    case 'maternityPhase':
    case 'marketingPreferences':
    case 'gestationData':
    case 'sharedAccess':
    case 'dependentes':
      return { tela: 'perfil' };
    default:
      return null;
  }
}

/** O dia do evento, no fuso das pacientes (São Paulo). */
export function diaDeUso(quando: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(quando);
}

// --- Documentos -----------------------------------------------------------

export const EventoDeUsoSchema = z.object({
  id: z.string(),
  evento: z.string(),
  brandId: brandIdField(),
  /** Hash da conta (HMAC): conta pessoas únicas sem guardar quem é. */
  pessoa: z.string(),
  dia: z.string(),
  ocorridoEm: z.coerce.date(),
  props: z.record(z.string()).default({}),
  origem: z.enum(['APP', 'API']),
  /** TTL do Firestore: o documento some depois desta data. */
  expiraEm: z.coerce.date(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});
export type EventoDeUsoGravado = z.infer<typeof EventoDeUsoSchema>;

const ContagemSchema = z.object({ total: z.number().int().default(0), pessoas: z.array(z.string()).default([]) });

export const ResumoDeEventoSchema = ContagemSchema.extend({
  /** Por propriedade e valor: `porProp.tela.chat = { total, pessoas }`. */
  porProp: z.record(z.record(ContagemSchema)).default({}),
});
export type ResumoDeEvento = z.infer<typeof ResumoDeEventoSchema>;

export const ResumoDaMarcaNoDiaSchema = z.object({
  /** Pessoas com qualquer evento no dia. */
  ativas: z.array(z.string()).default([]),
  eventos: z.record(ResumoDeEventoSchema).default({}),
});
export type ResumoDaMarcaNoDia = z.infer<typeof ResumoDaMarcaNoDiaSchema>;

export const ResumoDeUsoSchema = z.object({
  id: z.string(),
  dia: z.string(),
  /** Dia passado já somado: não muda mais. */
  fechado: z.boolean().default(false),
  marcas: z.record(ResumoDaMarcaNoDiaSchema).default({}),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});
export type ResumoDeUso = z.infer<typeof ResumoDeUsoSchema>;

// --- O painel ---------------------------------------------------------------

/** O que o painel da mantenedora recebe (`GET /api/mantenedor/analise`). */
export interface PainelDeUso {
  periodo: { de: string; ate: string; dias: number };
  marca: string | null;
  ativas: { noPeriodo: number; mediaPorDia: number; ultimos7: number; ultimos30: number; porDia: { dia: string; ativas: number; sessoes: number }[] };
  sessoes: number;
  funcionalidades: { chave: FuncionalidadeDeUso; rotulo: string; pessoas: number; pct: number }[];
  telas: { tela: string; rotulo: string; vistas: number; pessoas: number }[];
  botoes: { acao: string; destino: string; nome: string; toques: number; pessoas: number }[];
  bloqueios: { regra: string; nome: string; alvo: string; vistos: number; pessoasViram: number; acoes: number; pessoasAgiram: number; taxa: number }[];
  abertos: { tela: string; id: string; nome: string; aberturas: number; pessoas: number }[];
  servidor: { evento: string; rotulo: string; total: number; pessoas: number; top: { id: string; nome: string; total: number }[] }[];
}
